import { getPresetDataForNewCollection } from '../utils/getPresetDataForNewCollection'
import { getProgressBar } from '../../progress-bar/executeSteps'
import { truncateCollectionName } from '../truncateCollectionName'
import {
  createAndPopulatePresetCollection,
  runTransactionWithRetry,
  UNDO_REDO_LABELS,
} from '@wix/wix-data-client-common'
import { addPage as addPageWithRepeater } from './addPage'

import type {
  EditorSDK,
  FedopsLogger,
  I18n,
  Logger,
} from '@wix/wix-data-client-common-standalone'
import type { PresetDefinition } from '../../presets/types'
import type {
  AppApi,
  Editor3DynamicPagesBiParams,
} from '@wix/wix-data-client-common'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type { WixDataApi } from '@wix/wix-data-core'
import type { PageAddResult } from '../add-cms-pages/addCmsPages'
import { dynamicPageCustomRouterCreatePageClicked } from '@wix/bi-logger-wix-code-data/v2'

const interactionName = 'addCollectionAndPageWithRepeater'

type Params = {
  editorSdkProxy: EditorSDK
  preset: PresetDefinition
  collectionsApi: CollectionsApi
  wixDataApi: WixDataApi
  logger: Logger
  i18n: I18n
  fedopsLogger: FedopsLogger
  appApi: AppApi
  biParams: Editor3DynamicPagesBiParams
}

export const addCollectionAndPageWithRepeater = async ({
  editorSdkProxy,
  preset,
  collectionsApi,
  wixDataApi,
  logger,
  i18n,
  fedopsLogger,
  appApi,
  biParams,
}: Params) => {
  fedopsLogger.interactionStarted(interactionName)

  const progressBar = getProgressBar({
    editorSdkProxy,
    logger,
    totalSteps: 2,
    title: i18n.t('Editor3_Add_Pages_Progress_Bar_Title_Creating_Page'),
  })
  await progressBar.open()

  let result: PageAddResult

  try {
    const { collectionId, collectionName, schema, records } =
      await getPresetDataForNewCollection({
        preset,
        collectionsApi,
      })

    await progressBar.update({
      stepTitle: i18n.t(
        'Editor3_Add_Pages_Progress_Bar_Step_Title_Setting_Up_Collection',
        {
          collection_name: truncateCollectionName(collectionName),
        },
      ),
    })

    await createAndPopulatePresetCollection({
      collectionsApi,
      collectionId,
      collectionName,
      wixDataApi,
      schema,
      records,
    })

    appApi.refreshCollectionItemCounts([collectionId])

    await progressBar.update({
      stepTitle: i18n.t(
        'Editor3_Add_Pages_Progress_Bar_Step_Title_Adding_List_Page',
        {
          collection_name: truncateCollectionName(collectionName),
        },
      ),
    })

    result = await runTransactionWithRetry(editorSdkProxy, async () => {
      const result = await addPageWithRepeater({
        editorSdkProxy,
        preset,
        collectionName,
      })

      return {
        pageRef: result.pageRef,
        schema,
      }
    })

    await progressBar.update()
  } finally {
    await progressBar.close()
  }

  const { pageRef, schema } = result
  logger.bi(
    dynamicPageCustomRouterCreatePageClicked({
      page_type: 'page_with_repeater',
      collection_name: schema.displayName,
      collection_id: schema.id,
      is_auto_bound: false,
      new_collection: true,
      pageId: pageRef.id,
      action_type: 'add_dynamic_page',
      origin: biParams?.origin,
      template_id: preset.id,
    }),
  )
  await editorSdkProxy.history.add({
    label: UNDO_REDO_LABELS.ADD_PAGE_WITH_REPEATER,
  })

  fedopsLogger.interactionEnded(interactionName)

  await editorSdkProxy.pages.navigateTo({ pageRef })

  return { pageRef }
}
